import React, { useState, useEffect } from 'react';
import { getBeneficiaryDocuments, getDocumentsCountByStatus } from '../../services/beneficiaryDocumentService';
import DocumentWorkflowStatus from './DocumentWorkflowStatus';
import './BeneficiaryDocumentsList.css';

/**
 * Composant pour afficher la liste des documents d'un bénéficiaire
 */
const BeneficiaryDocumentsList = ({
  db,
  beneficiaryId,
  onDocumentSelect,
  onGenerateNew,
  refreshTrigger = 0,
  isClientView = false
}) => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [counters, setCounters] = useState({
    draft: 0,
    pending_signature: 0,
    signed: 0,
    archived: 0,
    total: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Types de documents disponibles
  const documentTypes = [
    { id: 'all', label: 'Tous les types' },
    { id: 'MANDAT', label: 'Contrat de Mandat' },
    { id: 'CGS', label: 'Conditions Générales' },
    { id: 'CONTRAT_TRAVAIL', label: 'Contrat de Travail' },
    { id: 'LIVRET_ACCUEIL', label: 'Livret d\'Accueil' },
    { id: 'REGLEMENT_BENEFICIAIRES', label: 'Règlement Intérieur' },
    { id: 'ATTESTATION_FISCALE', label: 'Attestation Fiscale' },
    { id: 'CHARTE_DROITS', label: 'Charte des Droits' }
  ];

  // Charger les documents
  useEffect(() => {
    if (beneficiaryId) {
      loadDocuments();
    }
  }, [beneficiaryId, refreshTrigger]);

  // Filtrer les documents
  useEffect(() => {
    applyFilters();
  }, [documents, statusFilter, typeFilter, searchTerm]);

  const loadDocuments = async () => {
    try {
      setIsLoading(true);

      // Charger tous les documents
      const docs = await getBeneficiaryDocuments(db, beneficiaryId);
      setDocuments(docs);

      // Charger les compteurs
      const counts = await getDocumentsCountByStatus(db, beneficiaryId);
      setCounters(counts);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }

    // Filtre par type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(doc => doc.documentType === typeFilter);
    }

    // Filtre par recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.documentName.toLowerCase().includes(searchLower) ||
        doc.documentType.toLowerCase().includes(searchLower)
      );
    }

    setFilteredDocuments(filtered);
  };

  const getDocumentTypeLabel = (type) => {
    const docType = documentTypes.find(dt => dt.id === type);
    return docType ? docType.label : type;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  if (isLoading && documents.length === 0) {
    return (
      <div className="beneficiary-documents-list loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement des documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="beneficiary-documents-list">
      {/* Header avec statistiques */}
      <div className="list-header">
        <div className="header-title">
          <h3>{isClientView ? 'Mes documents' : 'Documents'}</h3>
          {counters.total > 0 && (
            <span className="document-count">{counters.total} document{counters.total > 1 ? 's' : ''}</span>
          )}
        </div>
        {!isClientView && onGenerateNew && (
          <button onClick={onGenerateNew} className="btn-generate-new">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Générer un document
          </button>
        )}
      </div>

      {/* Statistiques rapides */}
      {counters.total > 0 && (
        <div className="stats-bar">
          <div className="stat-item" onClick={() => setStatusFilter('all')}>
            <span className="stat-label">Total</span>
            <span className="stat-value">{counters.total}</span>
          </div>
          <div className="stat-item" onClick={() => setStatusFilter('draft')}>
            <span className="stat-label">Brouillons</span>
            <span className="stat-value badge-draft-count">{counters.draft}</span>
          </div>
          <div className="stat-item" onClick={() => setStatusFilter('pending_signature')}>
            <span className="stat-label">En attente</span>
            <span className="stat-value badge-pending-count">{counters.pending_signature}</span>
          </div>
          <div className="stat-item" onClick={() => setStatusFilter('signed')}>
            <span className="stat-label">Signés</span>
            <span className="stat-value badge-signed-count">{counters.signed}</span>
          </div>
          <div className="stat-item" onClick={() => setStatusFilter('archived')}>
            <span className="stat-label">Archivés</span>
            <span className="stat-value badge-archived-count">{counters.archived}</span>
          </div>
        </div>
      )}

      {/* Filtres et recherche */}
      {documents.length > 0 && (
        <div className="filters-bar">
          <div className="search-box">
            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher un document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="clear-search">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          <div className="filter-group">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tous les statuts</option>
              <option value="draft">Brouillons</option>
              <option value="pending_signature">En attente de signature</option>
              <option value="signed">Signés</option>
              <option value="archived">Archivés</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              {documentTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Liste des documents */}
      <div className="documents-container">
        {filteredDocuments.length === 0 ? (
          <div className="empty-state">
            {documents.length === 0 ? (
              <>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="64" height="64">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h4>Aucun document</h4>
                <p>{isClientView ? 'Aucun document disponible pour le moment' : 'Commencez par générer un document pour ce bénéficiaire'}</p>
                {!isClientView && onGenerateNew && (
                  <button onClick={onGenerateNew} className="btn-primary">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Générer mon premier document
                  </button>
                )}
              </>
            ) : (
              <>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h4>Aucun résultat</h4>
                <p>Aucun document ne correspond à vos critères de recherche</p>
                <button onClick={() => { setSearchTerm(''); setStatusFilter('all'); setTypeFilter('all'); }} className="btn-secondary">
                  Réinitialiser les filtres
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="documents-grid">
            {filteredDocuments.map((document) => (
              <div
                key={document.id}
                className="document-card"
                onClick={() => onDocumentSelect(document)}
              >
                <div className="document-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>

                <div className="document-content">
                  <div className="document-header-info">
                    <h4 className="document-title">{document.documentName}</h4>
                    <DocumentWorkflowStatus status={document.status} />
                  </div>

                  <div className="document-meta">
                    <span className="document-type">{getDocumentTypeLabel(document.documentType)}</span>
                    <span className="document-date">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {formatDate(document.createdAt)}
                    </span>
                  </div>

                  {document.signature && (
                    <div className="document-signed-badge">
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Signé par {document.signature.signedBy}
                    </div>
                  )}
                </div>

                <div className="document-actions">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BeneficiaryDocumentsList;
